import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import {
  CalendarIcon,
  CaseIcon,
  CheckCircleIcon,
  Container,
  CustomBreadcrumbs,
  Desktop,
  LocationIcon,
  Mobile,
  NewText,
  ShareIcon,
} from "ui"
import { sharedColors as colors } from "ui/theme/colors"
import { Job } from "utils"
import { convertISODateString } from "utils/utils"
import FormSection from "./FormSection"

const StyledBreadCrumbs = styled.div`
  padding-top: 32px;
  padding-bottom: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 40px;
  }
`

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 24px 0px 16px;
  max-width: 750px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin: 32px 0px;
    width: fit-content;
  }
`
const StyledBox = styled(Box)`
  justify-self: baseline;
  margin-bottom: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
    margin-bottom: 60px;
  }
`
const StyledItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 300px;
  }
`

const StyledTextWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: baseline;
  p {
    max-width: 610px;
  }
  .advantage-text {
    font-size: 18px;
    line-height: 26px;

    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 20px;
      line-height: 29px;
    }
  }
`

const StyledDot = styled.div`
  width: 8px;
  height: 8px;
  background-color: ${(props) => props.theme.colors.propBlue};
  margin: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`

const StyledSplitContent = styled.div`
  margin-top: 32px;
  &:first-of-type {
    margin-top: 0px;
  }
  span {
    font-size: inherit !important;
  }
`

const StyledButton = styled(Button)`
  min-width: 153px;
  height: 48px;
  padding: 11px 22px;
  border: none;
  border-radius: 200px;
  outline: none;
  font-size: 18px;
  font-weight: 600;
  text-decoration: none;
  width: auto;
  background-color: ${(p) => p.theme.colors.buttonActiveBackgroundColor};
  color: ${(p) => p.theme.colors.buttonHoverTextColor};
  box-shadow: 0px 4px 8px 0px #00000029;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  :hover {
    background-color: ${(p) => p.theme.colors.buttonActiveBackgroundColor};
    color: ${(p) => p.theme.colors.buttonHoverTextColor};
  }
`

const StyledShareButton = styled(StyledButton)`
  padding: 8px 16px;
  background-color: transparent;
  height: 42px;
  color: ${(p) => p.theme.colors.black};
  :hover {
    background-color: transparent;
    color: ${(p) => p.theme.colors.black};
  }
`

function TopContent({ job, messages }: { job: Job; messages?: any }) {
  const onShare = async () => {
    const shareData = {
      title: job?.jobName || "",
      url: window.location.href,
    }
    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData)
      } catch (err) {
        console.error(err)
      }
    } else {
      navigator.clipboard.writeText(shareData.url)
    }
  }
  const subtitle = job?.professionName
  const fields = [job?.professionName, job?.subfield]
    ?.filter((field) => Boolean(field))
    ?.join(" | ")

  const position = job?.jobName
  const scopeJobName = job?.scopeJobName
  const content = job?.description
  const location = job?.areaName
  const date = convertISODateString(job?.startAdvertisingDate)
  const jobNumber = `מספר משרה - ${job?.orderId}`

  const splitContent = content?.split("\n\n") ? content?.split("\n\n") : []
  // const locale = useLocale()NOTE: Removed as per request, leaving for now in case of future need

  const isStrongEndingText = (text: string): boolean => {
    return (
      text.endsWith("</strong>:<br />") ||
      text.endsWith("</strong><br />") ||
      text.endsWith("</strong>?<br />")
    )
  }

  // const scrollToForm = () => {NOTE: Removed as per request, leaving for now in case of future need
  //   const target = document.getElementById("target-section")
  //   const top = (target?.offsetTop || 0) - 200
  //   window.scrollTo({ top, behavior: "smooth" })
  // }

  return (
    <Container>
      <StyledBreadCrumbs>
        <CustomBreadcrumbs
          current={{ title: "משרה", url: `/` }}
          noPadding={true}
          parents={[
            { title: "ראשי", url: "/" },
            { title: "קריירה", url: "/career" },
          ]}
          desktopFontSize={18}
          desktopLineHeight="100%"
          mobileFontSize={16}
          mobileLineHeight="100%"
        />
      </StyledBreadCrumbs>
      <Box display={"flex"} justifyContent={"space-between"} alignItems={"flex-start"}>
        <Box>
          <StyledItem>
            <NewText
              component="p"
              desktopFontSize={18}
              desktopLineHeight="100%"
              mobileFontSize={14}
              mobileLineHeight="20px"
              color={colors.gray}
            >
              {jobNumber}
            </NewText>
          </StyledItem>
          <NewText
            component="h1"
            desktopFontSize={40}
            desktopLineHeight="44px"
            desktopFontWeight={"700"}
            mobileFontSize={32}
            mobileLineHeight="36px"
            color={colors.corpBlue}
          >
            {position}
          </NewText>
          {/* <Desktop> NOTE: Removed as per request, leaving for now in case of future need
            <StyledButton onClick={scrollToForm} sx={{ marginTop: "16px" }}>
              {locale === "he" ? "הגשת מועמדות" : "Apply Now"}
            </StyledButton>
          </Desktop> */}
        </Box>
        <Desktop>
          <StyledShareButton onClick={onShare}>
            <ShareIcon width={20} height={20} />
            {messages?.sharePos ? messages?.sharePos : "שיתוף משרה"}
          </StyledShareButton>
        </Desktop>
      </Box>
      <StyledBox>
        <Box>
          <StyledGrid>
            {subtitle && (
              <StyledItem>
                <CaseIcon width={16} height={16} />
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  mobileFontSize={16}
                  mobileLineHeight="23px"
                  color={colors.black}
                >
                  {fields}
                </NewText>
              </StyledItem>
            )}
            {scopeJobName && (
              <StyledItem>
                <CalendarIcon width={16} height={16} />
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  mobileFontSize={16}
                  mobileLineHeight="23px"
                  color={colors.black}
                >
                  {scopeJobName}
                </NewText>
              </StyledItem>
            )}
            {location && (
              <StyledItem>
                <LocationIcon width={16} height={16} />
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  mobileFontSize={16}
                  mobileLineHeight="23px"
                  color={colors.black}
                >
                  {location}
                </NewText>
              </StyledItem>
            )}
            {date && (
              <StyledItem>
                <CheckCircleIcon width={16} height={16} />
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  mobileFontSize={16}
                  mobileLineHeight="23px"
                  color={colors.black}
                >
                  {date}
                </NewText>
              </StyledItem>
            )}
          </StyledGrid>
          <Mobile>
            <StyledShareButton onClick={onShare}>
              <ShareIcon width={20} height={20} />
              {messages?.sharePos ? messages?.sharePos : "שיתוף משרה"}
            </StyledShareButton>
          </Mobile>

          {splitContent.map((content, index) => {
            const splitContent = content.split("\n")
            return (
              <StyledSplitContent key={index.toString()}>
                <NewText
                  component="p"
                  desktopFontSize={20}
                  desktopLineHeight="29px"
                  mobileFontSize={18}
                  mobileLineHeight="26px"
                  desktopFontWeight={600}
                  color={colors.corpBlue}
                  dangerouslySetInnerHTML={{ __html: splitContent[0] }}
                />
                {splitContent.slice(1).map((item, index) => {
                  return (
                    /\S/.test(item) && (
                      <StyledTextWrapper key={index}>
                        {item.includes("-") && <StyledDot />}
                        <NewText
                          component="p"
                          desktopFontSize={isStrongEndingText(item) ? 20 : 18}
                          desktopLineHeight={
                            item === "<br />" &&
                            isStrongEndingText(splitContent.slice(1)?.[index + 1])
                              ? "32px"
                              : item === "<br />"
                              ? "8px"
                              : isStrongEndingText(item)
                              ? "29px"
                              : "26px"
                          }
                          mobileFontSize={18}
                          mobileLineHeight={item === "<br />" ? "32px" : "26px"}
                          desktopFontWeight={400}
                          color={isStrongEndingText(item) ? colors.corpBlue : colors.black}
                          dangerouslySetInnerHTML={{
                            __html: item
                              .replace(/^-/, "")
                              .replace(/>-/g, ">")
                              .replace("-&nbsp;", "")
                              .replace(
                                /^(.*?)\s*[-–]\s*([^<\s]+?)(?:<\/span><\/span>)?(<br \/>)?$/g,
                                `$1 - <strong class="advantage-text">$2</strong>$3`,
                              ),
                          }}
                        />
                      </StyledTextWrapper>
                    )
                  )
                })}
              </StyledSplitContent>
            )
          })}
        </Box>
      </StyledBox>
      <FormSection orderId={job?.orderId} jobType={job?.jobName} messages={messages} />
    </Container>
  )
}

export default TopContent
